/**
 * Composant Feuille Journalière de Travail (ERQ.RH.08)
 *
 * Interface de saisie des heures de travail par jour et par participant.
 * Optimisé pour utilisation sur tablette.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { getJWTPayload } from '../../utils/authUtils';
import { toLocalDateStr } from '../../utils/dateUtils';
import WorksheetHeader from './WorksheetHeader';
import WorksheetEntry from './WorksheetEntry';
import WorksheetFooter from './WorksheetFooter';
import QuickInterimForm from '../QuickInterimForm';
import EPIRefusalModal from './EPIRefusalModal';
import './DailyWorksheet.css';

const DailyWorksheet = ({
  prestationId,
  workDate,
  onClose,
  onSaved,
  readOnly = false
}) => {
  const { showSuccess, showError, showWarning } = useNotifications();

  // Détecter si l'utilisateur peut valider (permissions backend: work-sheets.validate, users.manage, availabilities.manage)
  const isValidator = useMemo(() => {
    const token = localStorage.getItem('token');
    const payload = getJWTPayload(token);
    const permissions = payload?.permissions || [];
    return permissions.includes('all') ||
      permissions.includes('work-sheets.validate') ||
      permissions.includes('availabilities.manage') ||
      permissions.includes('users.manage');
  }, []);

  // États
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const [prestation, setPrestation] = useState(null);
  const [prestationType, setPrestationType] = useState('prestation'); // prestation, blocked, note
  const [entries, setEntries] = useState([]);
  const [headerData, setHeaderData] = useState({
    location: '',
    distance_km: '',
    notes: '',
    observation: ''
  });
  const [isDirty, setIsDirty] = useState(false);

  // État pour la modale d'ajout d'intérimaire
  const [showInterimForm, setShowInterimForm] = useState(false);

  // État pour la modale de refus EPI (protections auditives)
  const [epiRefusalTarget, setEpiRefusalTarget] = useState(null);

  // État pour la logique nuit hôtel : si quelqu'un avait nuit hôtel la veille
  const [hadHotelNightYesterday, setHadHotelNightYesterday] = useState({});

  // État des sections accordéon (toutes ouvertes par défaut)
  const [expandedSections, setExpandedSections] = useState({
    transport: true,
    horaires: true,
    epi: true,
    repas: true,
    heures: true
  });

  // Toggle d'une section accordéon
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Liste des véhicules disponibles (chargée depuis l'API)
  const [availableVehicles, setAvailableVehicles] = useState([]);

  // Véhicules sélectionnés sur cette feuille [{id: uuid, vehicleId: uuid}]
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  // Ajouter un véhicule (avec un vehicleId vide par défaut)
  const addVehicle = () => {
    setSelectedVehicles(prev => [
      ...prev,
      { id: crypto.randomUUID(), vehicleId: '' }
    ]);
    setIsDirty(true);
  };

  // Retirer le dernier véhicule
  const removeVehicle = () => {
    if (selectedVehicles.length === 0) return;
    const vehicleToRemove = selectedVehicles[selectedVehicles.length - 1];
    setSelectedVehicles(prev => prev.slice(0, -1));
    // Retirer ce véhicule de toutes les entries
    setEntries(prev => prev.map(entry => ({
      ...entry,
      vehicles: (entry.vehicles || []).filter(v => v.slot !== selectedVehicles.length - 1)
    })));
    setIsDirty(true);
  };

  // Changer le véhicule sélectionné pour un slot
  const changeVehicle = (slotIndex, vehicleId) => {
    setSelectedVehicles(prev => prev.map((v, idx) =>
      idx === slotIndex ? { ...v, vehicleId } : v
    ));
    setIsDirty(true);
  };

  // Charger les données
  const loadData = useCallback(async () => {
    // Protection : ne pas appeler l'API si prestationId est manquant
    if (!prestationId) {
      console.error('DailyWorksheet: prestationId manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        API_ENDPOINTS.WORKSHEET_BY_PRESTATION_DAY(prestationId, workDate),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur chargement');
      }

      if (data.data.exists) {
        // Feuille existante
        setWorksheet(data.data);
        setPrestationType(data.data.prestation_type || 'prestation');
        setHeaderData({
          location: data.data.location || '',
          distance_km: data.data.distance_km || '',
          notes: data.data.notes || '',
          observation: data.data.observation || ''
        });
        const loadedEntries = data.data.entries || [];
        setEntries(loadedEntries);

        // Initialiser les véhicules sélectionnés à partir des entries
        if (data.data.used_vehicles && data.data.used_vehicles.length > 0) {
          // Reconstruire selectedVehicles à partir des vehicles des entries
          const vehicleSlots = new Map();
          for (const entry of loadedEntries) {
            for (const v of (entry.vehicles || [])) {
              if (!vehicleSlots.has(v.slot)) {
                vehicleSlots.set(v.slot, v.vehicle_id);
              }
            }
          }
          const sortedSlots = Array.from(vehicleSlots.entries()).sort((a, b) => a[0] - b[0]);
          setSelectedVehicles(sortedSlots.map(([slot, vehicleId]) => ({
            id: crypto.randomUUID(),
            vehicleId
          })));
        }
      } else {
        // Nouvelle feuille - initialiser avec les participants
        setPrestation(data.data.prestation);
        setPrestationType(data.data.prestation?.prestation_type || 'prestation');
        // Pré-remplir la distance: lieu de mise si défini, sinon client
        const defaultDistance = data.data.prestation?.default_distance_km ||
                               data.data.prestation?.location_distance_km ||
                               data.data.prestation?.client_distance_km || '';
        setHeaderData({
          location: data.data.prestation?.location_city || data.data.prestation?.group_city || '',
          distance_km: defaultDistance ? String(defaultDistance) : '',
          notes: '',
          observation: ''
        });

        // Initialiser les entrées vides pour chaque participant
        const initialEntries = (data.data.participants || []).map(p => ({
          user_id: p.user_id,
          user_name: p.user_name,
          first_name: p.first_name,
          last_name: p.last_name,
          is_manager: p.is_manager || false,
          employee_status: p.employee_status,
          is_temporary: p.is_temporary,
          is_on_site: p.is_on_site || false,
          is_absent: false,
          absence_reason: '',
          truck_outbound: false,
          truck_return: false,
          van_outbound: 0,
          van_return: 0,
          departure_time: '',
          arrival_site_1: '',
          departure_site_1: '',
          arrival_site_2: '',
          departure_site_2: '',
          arrival_depot: '',
          safety_shoes: null,
          hearing_protection: null,
          restaurant_lunch: false,
          hotel_nights: 0,
          work_hours: null,
          travel_hours: null,
          specific_travel_hours: null,
          misc_hours: null,
          total_hours: null,
          accounting_notes: ''
        }));

        setEntries(initialEntries);
      }

      // Charger les données de nuit hôtel de la veille (même prestation)
      try {
        const yesterday = new Date(workDate);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = toLocalDateStr(yesterday);

        const yesterdayResponse = await fetch(
          API_ENDPOINTS.WORKSHEET_BY_PRESTATION_DAY(prestationId, yesterdayStr),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const yesterdayData = await yesterdayResponse.json();

        if (yesterdayData.success && yesterdayData.data.exists) {
          // Construire un objet { user_id: true/false } pour les nuits hôtel de la veille
          const hotelMap = {};
          (yesterdayData.data.entries || []).forEach(entry => {
            if (entry.hotel_night) {
              hotelMap[entry.user_id] = true;
            }
          });
          setHadHotelNightYesterday(hotelMap);
        }
      } catch (err) {
        // Pas grave si on ne peut pas charger les données de la veille
        console.debug('Pas de données J-1:', err);
      }
    } catch (error) {
      console.error('Erreur chargement feuille:', error);
      showError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [prestationId, workDate, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Charger les véhicules disponibles
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(API_ENDPOINTS.VEHICLES, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          setAvailableVehicles(data.data);
        }
      } catch (error) {
        console.error('Erreur chargement véhicules:', error);
      }
    };
    loadVehicles();
  }, []);

  // Parse time HH:MM to minutes
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return null;
  };

  // Champs qui impactent le calcul des heures
  const RECALC_FIELDS = [
    'arrival_site_1', 'departure_site_1', 'arrival_site_2', 'departure_site_2',
    'truck_outbound', 'truck_return', 'van_outbound', 'van_return',
    'is_on_site', 'misc_hours', 'specific_travel_hours', 'vehicles', 'is_absent'
  ];

  /**
   * Calcul des heures de route selon les nouvelles règles
   * IMPORTANT: Cette fonction doit être définie AVANT les callbacks qui l'utilisent
   * @param {Object} entry - L'entrée du participant
   * @param {number} distanceKm - Distance en km
   * @param {Array} allEntries - Toutes les entrées (pour comparer heures départ camion)
   * @param {boolean} hadHotelYesterday - Si nuit hôtel la veille pour ce participant
   * @returns {string|null} Heures de route calculées
   */
  const calculateTravelHoursAdvanced = useCallback((entry, distanceKm, allEntries, hadHotelYesterday) => {
    // Absent = pas de route
    if (entry.is_absent) return null;
    // Sur place = pas de route
    if (entry.is_on_site) return null;
    if (!distanceKm || distanceKm <= 0) return null;

    // Déterminer si camion/fourgon aller/retour (legacy + nouvelle table vehicles)
    const hasTruckOutbound = entry.truck_outbound || (entry.vehicles || []).some(v => v.is_outbound && v.is_truck);
    const hasTruckReturn = entry.truck_return || (entry.vehicles || []).some(v => v.is_return && v.is_truck);
    const hasVanOutbound = entry.van_outbound || (entry.vehicles || []).some(v => v.is_outbound && !v.is_truck);
    const hasVanReturn = entry.van_return || (entry.vehicles || []).some(v => v.is_return && !v.is_truck);
    const hasOutbound = hasTruckOutbound || hasVanOutbound;
    const hasReturn = hasTruckReturn || hasVanReturn;

    // Déterminer le type de jour (hôtel)
    const hasHotelTonight = entry.hotel_night || false;
    const isHotelDepartureDay = hasHotelTonight && !hadHotelYesterday; // Jour de l'aller
    const isHotelIntermediateDay = hasHotelTonight && hadHotelYesterday; // Jour intermédiaire
    const isHotelReturnDay = !hasHotelTonight && hadHotelYesterday; // Jour du retour
    const isHotelContext = isHotelDepartureDay || isHotelIntermediateDay || isHotelReturnDay;

    // ===== RÈGLES HÔTEL MULTI-JOURS =====
    if (isHotelContext) {
      if (isHotelIntermediateDay) {
        // Jours intermédiaires : pas de route
        return '0.00';
      }
      if (isHotelDepartureDay) {
        // Jour de l'aller : conducteurs avec coeff véhicule, passagers KM / 60 × 1
        if (hasOutbound) {
          let multiplier = 1;
          if (hasTruckOutbound) {
            multiplier = 1.25;
          } else if (hasVanOutbound) {
            // Bonus convoi si même heure de départ que le camion
            const truckDriver = allEntries.find(e =>
              (e.truck_outbound || (e.vehicles || []).some(v => v.is_truck && v.is_outbound)) &&
              e.departure_time
            );
            if (truckDriver && entry.departure_time === truckDriver.departure_time) {
              multiplier = 1.25;
            }
          }
          return ((distanceKm / 60) * multiplier).toFixed(2);
        }
        return (distanceKm / 60).toFixed(2);
      }
      if (isHotelReturnDay) {
        // Jour du retour hôtel : tout le monde part ensemble → convoi automatique
        if (hasReturn) {
          let multiplier = 1;
          if (hasTruckReturn) {
            multiplier = 1.25;
          } else if (hasVanReturn) {
            // Retour hôtel = convoi : bonus si un camion est dans le groupe
            const hasTruckInGroup = allEntries.some(e =>
              e.truck_return ||
              (e.vehicles || []).some(v => v.is_truck && v.is_return)
            );
            if (hasTruckInGroup) multiplier = 1.25;
          }
          return ((distanceKm / 60) * multiplier).toFixed(2);
        }
        return (distanceKm / 60).toFixed(2);
      }
    }

    // ===== RÈGLES TRAJET >= 120 KM (DÉPLACEMENT) =====
    if (distanceKm >= 120) {
      // Base : tout le monde a KM / 60 × 1
      let multiplier = 1;

      // Bonus si camion aller OU retour : × 1.25 (remplace la base)
      if (hasTruckOutbound || hasTruckReturn) {
        multiplier = 1.25;
      }
      // Bonus si fourgon aller OU retour ET même heure départ que camion : × 1.25
      else if (hasVanOutbound || hasVanReturn) {
        // Trouver l'heure de départ du camion (legacy + nouveau système véhicules)
        const truckDriver = allEntries.find(e =>
          (e.truck_outbound || (e.vehicles || []).some(v => v.is_truck && v.is_outbound)) &&
          e.departure_time
        );
        if (truckDriver && entry.departure_time === truckDriver.departure_time) {
          multiplier = 1.25;
        }
      }

      return ((distanceKm / 60) * multiplier).toFixed(2);
    }

    // ===== RÈGLES TRAJET < 120 KM =====
    let multiplier = 1; // Aucun véhicule

    if (hasOutbound && hasReturn) {
      multiplier = 2; // Aller ET retour
    } else if (hasOutbound || hasReturn) {
      multiplier = 1.5; // Aller OU retour
    }

    return ((distanceKm / 60) * multiplier).toFixed(2);
  }, []);

  // Mettre à jour un véhicule pour un participant (slot, is_outbound ou is_return)
  const handleVehicleChange = useCallback((userId, slotIndex, field, value) => {
    setEntries(prev => prev.map(entry => {
      if (entry.user_id !== userId) return entry;

      const vehicles = [...(entry.vehicles || [])];
      const existingIdx = vehicles.findIndex(v => v.slot === slotIndex);

      if (existingIdx >= 0) {
        // Mettre à jour le véhicule existant
        vehicles[existingIdx] = { ...vehicles[existingIdx], [field]: value };
        // Si ni outbound ni return, supprimer le véhicule
        if (!vehicles[existingIdx].is_outbound && !vehicles[existingIdx].is_return) {
          vehicles.splice(existingIdx, 1);
        }
      } else if (value) {
        // Ajouter un nouveau véhicule
        const vehicleId = selectedVehicles[slotIndex]?.vehicleId;
        if (vehicleId) {
          vehicles.push({
            slot: slotIndex,
            vehicle_id: vehicleId,
            is_outbound: field === 'is_outbound' ? value : false,
            is_return: field === 'is_return' ? value : false
          });
        }
      }

      const updated = { ...entry, vehicles };

      // Recalculer les heures de route avec nouvelles règles
      const distanceKm = parseFloat(headerData.distance_km) || 0;
      if (!updated.is_manager) {
        const hadHotelYesterday = hadHotelNightYesterday[updated.user_id] || false;
        // On utilise les entries actuelles avec cette entry mise à jour
        const entriesForCalc = prev.map(e => e.user_id === entry.user_id ? updated : e);
        updated.travel_hours = calculateTravelHoursAdvanced(updated, distanceKm, entriesForCalc, hadHotelYesterday);
      }

      // Total
      updated.total_hours = (
        parseFloat(updated.work_hours || 0) +
        parseFloat(updated.travel_hours || 0) +
        parseFloat(updated.specific_travel_hours || 0) +
        parseFloat(updated.misc_hours || 0)
      ).toFixed(2);

      return updated;
    }));
    setIsDirty(true);
  }, [headerData.distance_km, selectedVehicles, hadHotelNightYesterday, calculateTravelHoursAdvanced]);

  // Mise à jour d'une entrée
  const handleEntryChange = useCallback((userId, field, value) => {
    setEntries(prev => prev.map(entry => {
      if (entry.user_id === userId) {
        const updated = { ...entry, [field]: value };
        // Cadres: pas de recalcul d'heures
        if (updated.is_manager) {
          return updated;
        }
        // Recalculer les heures si le champ impacte le calcul
        if (RECALC_FIELDS.includes(field)) {
          if (updated.is_absent) {
            updated.work_hours = null;
            updated.travel_hours = null;
            updated.total_hours = '0.00';
            return updated;
          }
          const distanceKm = parseFloat(headerData.distance_km) || 0;
          let workMinutes = 0;

          // Période 1
          if (updated.arrival_site_1 && updated.departure_site_1) {
            const arr1 = parseTime(updated.arrival_site_1);
            const dep1 = parseTime(updated.departure_site_1);
            if (arr1 !== null && dep1 !== null) {
              workMinutes += (dep1 - arr1);
            }
          }

          // Période 2
          if (updated.arrival_site_2 && updated.departure_site_2) {
            const arr2 = parseTime(updated.arrival_site_2);
            const dep2 = parseTime(updated.departure_site_2);
            if (arr2 !== null && dep2 !== null) {
              workMinutes += (dep2 - arr2);
            }
          }

          updated.work_hours = workMinutes > 0 ? (workMinutes / 60).toFixed(2) : null;

          // Calcul route avec nouvelles règles
          const hadHotelYesterday = hadHotelNightYesterday[updated.user_id] || false;
          // Pour le calcul, on a besoin de toutes les entries (pour comparer heure départ camion)
          // On utilise les entries actuelles avec cette entry mise à jour
          const entriesForCalc = prev.map(e => e.user_id === userId ? updated : e);
          updated.travel_hours = calculateTravelHoursAdvanced(updated, distanceKm, entriesForCalc, hadHotelYesterday);

          // Total
          updated.total_hours = (
            parseFloat(updated.work_hours || 0) +
            parseFloat(updated.travel_hours || 0) +
            parseFloat(updated.specific_travel_hours || 0) +
            parseFloat(updated.misc_hours || 0)
          ).toFixed(2);
        }
        return updated;
      }
      return entry;
    }));
    setIsDirty(true);
  }, [headerData.distance_km, hadHotelNightYesterday, calculateTravelHoursAdvanced]);

  // Appliquer une valeur à tous les participants
  const applyToAll = useCallback((field, value) => {
    setEntries(prev => {
      // D'abord, appliquer la valeur à tous
      const updatedEntries = prev.map(entry => ({ ...entry, [field]: value }));
      // Ensuite, recalculer les heures pour tous (car le calcul dépend des autres entries)
      if (RECALC_FIELDS.includes(field)) {
        const distanceKm = parseFloat(headerData.distance_km) || 0;
        return updatedEntries.map(updated => {
          if (updated.is_absent) {
            updated.work_hours = null;
            updated.travel_hours = null;
            updated.total_hours = '0.00';
            return updated;
          }
          let workMinutes = 0;

          if (updated.arrival_site_1 && updated.departure_site_1) {
            const arr1 = parseTime(updated.arrival_site_1);
            const dep1 = parseTime(updated.departure_site_1);
            if (arr1 !== null && dep1 !== null) workMinutes += (dep1 - arr1);
          }
          if (updated.arrival_site_2 && updated.departure_site_2) {
            const arr2 = parseTime(updated.arrival_site_2);
            const dep2 = parseTime(updated.departure_site_2);
            if (arr2 !== null && dep2 !== null) workMinutes += (dep2 - arr2);
          }
          updated.work_hours = workMinutes > 0 ? (workMinutes / 60).toFixed(2) : null;

          // Calcul route avec nouvelles règles
          const hadHotelYesterday = hadHotelNightYesterday[updated.user_id] || false;
          updated.travel_hours = calculateTravelHoursAdvanced(updated, distanceKm, updatedEntries, hadHotelYesterday);

          updated.total_hours = (
            parseFloat(updated.work_hours || 0) +
            parseFloat(updated.travel_hours || 0) +
            parseFloat(updated.specific_travel_hours || 0) +
            parseFloat(updated.misc_hours || 0)
          ).toFixed(2);
          return updated;
        });
      }
      return updatedEntries;
    });
    setIsDirty(true);
  }, [headerData.distance_km, hadHotelNightYesterday, calculateTravelHoursAdvanced]);

  // Copier la première valeur non vide vers tous
  const copyFirstToAll = useCallback((field) => {
    const firstValue = entries.find(e => e[field] !== '' && e[field] !== null && e[field] !== undefined)?.[field];
    if (firstValue !== undefined) {
      setEntries(prev => {
        // D'abord, appliquer la valeur à tous
        const updatedEntries = prev.map(entry => ({ ...entry, [field]: firstValue }));
        // Ensuite, recalculer les heures pour tous
        if (RECALC_FIELDS.includes(field)) {
          const distanceKm = parseFloat(headerData.distance_km) || 0;
          return updatedEntries.map(updated => {
            if (updated.is_absent) {
              updated.work_hours = null;
              updated.travel_hours = null;
              updated.total_hours = '0.00';
              return updated;
            }
            let workMinutes = 0;

            if (updated.arrival_site_1 && updated.departure_site_1) {
              const arr1 = parseTime(updated.arrival_site_1);
              const dep1 = parseTime(updated.departure_site_1);
              if (arr1 !== null && dep1 !== null) workMinutes += (dep1 - arr1);
            }
            if (updated.arrival_site_2 && updated.departure_site_2) {
              const arr2 = parseTime(updated.arrival_site_2);
              const dep2 = parseTime(updated.departure_site_2);
              if (arr2 !== null && dep2 !== null) workMinutes += (dep2 - arr2);
            }
            updated.work_hours = workMinutes > 0 ? (workMinutes / 60).toFixed(2) : null;

            // Calcul route avec nouvelles règles
            const hadHotelYesterday = hadHotelNightYesterday[updated.user_id] || false;
            updated.travel_hours = calculateTravelHoursAdvanced(updated, distanceKm, updatedEntries, hadHotelYesterday);

            updated.total_hours = (
              parseFloat(updated.work_hours || 0) +
              parseFloat(updated.travel_hours || 0) +
              parseFloat(updated.specific_travel_hours || 0) +
              parseFloat(updated.misc_hours || 0)
            ).toFixed(2);
            return updated;
          });
        }
        return updatedEntries;
      });
      setIsDirty(true);
    }
  }, [entries, headerData.distance_km, hadHotelNightYesterday, calculateTravelHoursAdvanced]);

  // Mise à jour header
  const handleHeaderChange = useCallback((field, value) => {
    setHeaderData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  // Calculer les heures pour une entrée (utilise les nouvelles règles)
  const calculateHours = useCallback((entry, allEntriesOverride = null) => {
    if (entry.is_absent) {
      return { work_hours: null, travel_hours: null, total_hours: '0.00' };
    }
    let workMinutes = 0;

    // Période 1
    if (entry.arrival_site_1 && entry.departure_site_1) {
      const arr1 = parseTime(entry.arrival_site_1);
      const dep1 = parseTime(entry.departure_site_1);
      if (arr1 !== null && dep1 !== null) {
        workMinutes += (dep1 - arr1);
      }
    }

    // Période 2
    if (entry.arrival_site_2 && entry.departure_site_2) {
      const arr2 = parseTime(entry.arrival_site_2);
      const dep2 = parseTime(entry.departure_site_2);
      if (arr2 !== null && dep2 !== null) {
        workMinutes += (dep2 - arr2);
      }
    }

    const workHours = workMinutes > 0 ? (workMinutes / 60).toFixed(2) : null;

    // Calcul route avec nouvelles règles
    const distanceKm = parseFloat(headerData.distance_km) || 0;
    const allEntriesForCalc = allEntriesOverride || entries;
    const hadHotelYesterday = hadHotelNightYesterday[entry.user_id] || false;
    const travelHours = calculateTravelHoursAdvanced(entry, distanceKm, allEntriesForCalc, hadHotelYesterday);

    const total = (
      parseFloat(workHours || 0) +
      parseFloat(travelHours || 0) +
      parseFloat(entry.specific_travel_hours || 0) +
      parseFloat(entry.misc_hours || 0)
    ).toFixed(2);

    return { work_hours: workHours, travel_hours: travelHours, total_hours: total };
  }, [headerData.distance_km, entries, hadHotelNightYesterday, calculateTravelHoursAdvanced]);

  // Recalculer toutes les heures
  const recalculateAllHours = useCallback(() => {
    setEntries(prev => prev.map(entry => {
      const calculated = calculateHours(entry);
      return { ...entry, ...calculated };
    }));
  }, [calculateHours]);

  // Effet pour recalculer quand la distance change
  useEffect(() => {
    if (headerData.distance_km) {
      recalculateAllHours();
    }
  }, [headerData.distance_km, recalculateAllHours]);

  // Convertir une valeur vide en null pour les champs numériques
  const toNumericOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  };

  // Convertir une valeur vide en null pour les champs texte/time
  const toStringOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    return value;
  };

  // Sauvegarder
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Recalculer les heures et nettoyer les valeurs avant sauvegarde
      const entriesWithHours = entries.map(entry => {
        const calculated = calculateHours(entry);
        return {
          ...entry,
          ...calculated,
          // Convertir les champs time vides en null
          departure_time: toStringOrNull(entry.departure_time),
          arrival_site_1: toStringOrNull(entry.arrival_site_1),
          departure_site_1: toStringOrNull(entry.departure_site_1),
          arrival_site_2: toStringOrNull(entry.arrival_site_2),
          departure_site_2: toStringOrNull(entry.departure_site_2),
          arrival_depot: toStringOrNull(entry.arrival_depot),
          // Convertir les champs numériques vides en null
          specific_travel_hours: toNumericOrNull(entry.specific_travel_hours),
          misc_hours: toNumericOrNull(entry.misc_hours),
          hotel_nights: toNumericOrNull(entry.hotel_nights) || 0,
          work_hours: toNumericOrNull(calculated.work_hours),
          travel_hours: toNumericOrNull(calculated.travel_hours),
          total_hours: toNumericOrNull(calculated.total_hours)
        };
      });

      const payload = {
        prestation_id: prestationId,
        work_date: workDate,
        location: headerData.location,
        distance_km: toNumericOrNull(headerData.distance_km),
        notes: headerData.notes,
        observation: headerData.observation,
        entries: entriesWithHours,
        created_by_user_id: user.id
      };

      let response;

      if (worksheet?.id) {
        // Mise à jour
        response = await fetch(API_ENDPOINTS.WORKSHEET_BY_ID(worksheet.id), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        // Création
        response = await fetch(API_ENDPOINTS.WORKSHEETS, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur sauvegarde');
      }

      showSuccess('Feuille sauvegardée');
      setIsDirty(false);

      if (onSaved) {
        onSaved(data.data);
      }

      // Recharger pour avoir l'ID si création
      if (!worksheet?.id) {
        loadData();
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showError(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Soumettre pour validation
  const handleSubmit = async () => {
    if (!worksheet?.id) {
      showWarning('Veuillez d\'abord sauvegarder la feuille');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_SUBMIT(worksheet.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur soumission');
      }

      showSuccess('Feuille soumise pour validation');

      if (onSaved) {
        onSaved();
      }

      loadData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      showError(error.message || 'Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  // Valider la feuille (validateur uniquement)
  const handleValidate = async () => {
    if (!worksheet?.id) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_VALIDATE(worksheet.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur validation');
      }

      showSuccess('Feuille validée');

      if (onSaved) {
        onSaved();
      }

      loadData();
    } catch (error) {
      console.error('Erreur validation:', error);
      showError(error.message || 'Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  // Contrôler la feuille (Comptabilité/Admin) : validated → checked (terminal)
  const handleCheck = async () => {
    if (!worksheet?.id) return;
    if (!window.confirm('Contrôler cette feuille ?\n\nCette action est définitive : la feuille ne pourra plus être modifiée ni dévalidée.')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_CHECK(worksheet.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur contrôle');
      }

      showSuccess('Feuille contrôlée');

      if (onSaved) {
        onSaved();
      }

      loadData();
    } catch (error) {
      console.error('Erreur contrôle:', error);
      showError(error.message || 'Erreur lors du contrôle');
    } finally {
      setSaving(false);
    }
  };

  // Dévalider la feuille (validateur uniquement) - rouvre pour modification
  const handleInvalidate = async () => {
    if (!worksheet?.id) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_INVALIDATE(worksheet.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Réouverture pour modification' })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur dévalidation');
      }

      showSuccess('Feuille dévalidée - vous pouvez maintenant la modifier');

      loadData();
    } catch (error) {
      console.error('Erreur dévalidation:', error);
      showError(error.message || 'Erreur lors de la dévalidation');
    } finally {
      setSaving(false);
    }
  };

  // Créer une entrée vide pour un intérimaire
  const createInterimEntry = useCallback((user, isNew = false) => ({
    user_id: user.id,
    user_name: user.full_name || `${user.first_name} ${user.last_name}`,
    first_name: user.first_name,
    last_name: user.last_name,
    is_manager: false,
    employee_status: 'Interim',
    is_temporary: true,
    is_on_site: false,
    is_absent: false,
    absence_reason: '',
    truck_outbound: false,
    truck_return: false,
    van_outbound: 0,
    van_return: 0,
    departure_time: '',
    arrival_site_1: '',
    departure_site_1: '',
    arrival_site_2: '',
    departure_site_2: '',
    arrival_depot: '',
    safety_shoes: null,
    hearing_protection: null,
    restaurant_lunch: false,
    hotel_nights: 0,
    hotel_night: false,
    work_hours: null,
    travel_hours: null,
    misc_hours: null,
    total_hours: null,
    accounting_notes: '',
    is_new_interim: isNew // Marquer si créé par chef d'équipe
  }), []);

  // Gérer l'ajout d'un intérimaire nouvellement créé
  const handleInterimCreated = useCallback((newUser) => {
    // Vérifier si pas déjà dans les entrées
    if (entries.some(e => e.user_id === newUser.id)) {
      showWarning(`${newUser.first_name} ${newUser.last_name} est déjà dans la feuille`);
      setShowInterimForm(false);
      return;
    }

    const newEntry = createInterimEntry(newUser, true);
    setEntries(prev => [...prev, newEntry]);
    setIsDirty(true);
    showSuccess(`${newUser.first_name} ${newUser.last_name} ajouté à la feuille`);
    setShowInterimForm(false);
  }, [entries, createInterimEntry, showSuccess, showWarning]);

  // Gérer la sélection d'un intérimaire existant
  const handleSelectExistingInterim = useCallback((existingUser) => {
    // Vérifier si pas déjà dans les entrées
    if (entries.some(e => e.user_id === existingUser.id)) {
      showWarning(`${existingUser.full_name} est déjà dans la feuille`);
      setShowInterimForm(false);
      return;
    }

    const newEntry = createInterimEntry(existingUser, false);
    setEntries(prev => [...prev, newEntry]);
    setIsDirty(true);
    showSuccess(`${existingUser.full_name} ajouté à la feuille`);
    setShowInterimForm(false);
  }, [entries, createInterimEntry, showSuccess, showWarning]);

  // Statut actuel
  const currentStatus = worksheet?.status_code || 'draft';
  const isEditable = !readOnly && (
    currentStatus === 'draft' ||
    currentStatus === 'rejected' ||
    (currentStatus === 'submitted' && isValidator)
  );

  if (loading) {
    return (
      <div className="daily-worksheet loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="daily-worksheet">
      <WorksheetHeader
        workDate={workDate}
        prestation={prestation || worksheet}
        headerData={headerData}
        onHeaderChange={handleHeaderChange}
        status={currentStatus}
        isEditable={isEditable}
        onClose={onClose}
        worksheetId={worksheet?.id}
      />

      <div className="worksheet-entries">
        <table className="entries-table transposed">
          <thead>
            <tr>
              <th className="row-label"></th>
              {entries.map(entry => (
                <th key={entry.user_id} className={`col-participant ${entry.is_absent ? 'is-absent' : ''}`}>
                  <div className="participant-name">
                    {entry.first_name}
                    <span className="last-name">{entry.last_name}</span>
                  </div>
                  {entry.is_temporary && <span className="badge-temp">INT</span>}
                  {entry.is_absent && <span className="badge-absent">ABS</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Absent - ligne prioritaire en haut */}
            <tr className="field-row absence-row">
              <td className="row-label absent-label">
                ❌ Absent
              </td>
              {entries.map(entry => (
                <td key={entry.user_id} className={`cell-checkbox cell-absent ${entry.is_absent ? 'is-absent' : ''}`}>
                  <input
                    type="checkbox"
                    checked={entry.is_absent || false}
                    onChange={(e) => handleEntryChange(entry.user_id, 'is_absent', e.target.checked)}
                    disabled={!isEditable}
                    className="absent-checkbox"
                  />
                </td>
              ))}
            </tr>

            {/* Sur place */}
            <tr className="field-row">
              <td className="row-label">
                Sur place
                {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('is_on_site', true)} title="Cocher tous">✓</button>}
              </td>
              {entries.map(entry => (
                <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                  <input
                    type="checkbox"
                    checked={entry.is_on_site || false}
                    onChange={(e) => handleEntryChange(entry.user_id, 'is_on_site', e.target.checked)}
                    disabled={!isEditable || entry.is_absent}
                  />
                </td>
              ))}
            </tr>

            {/* Sections transport (masquees pour blocages et notes) */}
            {prestationType === 'prestation' && (
              <>
                {/* Header Transport - accordéon avec boutons +/- pour véhicules */}
                <tr className="section-header section-accordion">
                  <td colSpan={entries.length + 1} onClick={() => toggleSection('transport')}>
                    <span className={`accordion-icon ${expandedSections.transport ? 'expanded' : ''}`}>▶</span>
                    Transport
                    {isEditable && expandedSections.transport && (
                      <span className="van-controls" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="van-btn van-remove"
                          onClick={removeVehicle}
                          disabled={selectedVehicles.length <= 0}
                          title="Supprimer un véhicule"
                        >−</button>
                        <span className="van-count">
                          {selectedVehicles.length === 0 ? 'Pas de véhicule' : `${selectedVehicles.length} véhicule${selectedVehicles.length > 1 ? 's' : ''}`}
                        </span>
                        <button
                          className="van-btn van-add"
                          onClick={addVehicle}
                          disabled={selectedVehicles.length >= 5}
                          title="Ajouter un véhicule"
                        >+</button>
                      </span>
                    )}
                  </td>
                </tr>

                {expandedSections.transport && (
                  <>
                    {/* Camion aller */}
                    <tr className="field-row">
                      <td className="row-label">
                        Camion →
                        {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('truck_outbound', true)} title="Cocher tous">✓</button>}
                      </td>
                      {entries.map(entry => (
                        <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                          <input
                            type="checkbox"
                            checked={entry.truck_outbound || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              handleEntryChange(entry.user_id, 'truck_outbound', checked);
                              // Si camion coché, désactiver fourgon aller
                              if (checked && entry.van_outbound > 0) {
                                handleEntryChange(entry.user_id, 'van_outbound', 0);
                              }
                            }}
                            disabled={!isEditable || entry.is_on_site || entry.is_absent}
                          />
                        </td>
                      ))}
                    </tr>

                    {/* Camion retour */}
                    <tr className="field-row">
                      <td className="row-label">
                        Camion ←
                        {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('truck_return', true)} title="Cocher tous">✓</button>}
                      </td>
                      {entries.map(entry => (
                        <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                          <input
                            type="checkbox"
                            checked={entry.truck_return || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              handleEntryChange(entry.user_id, 'truck_return', checked);
                              // Si camion coché, désactiver fourgon retour
                              if (checked && entry.van_return > 0) {
                                handleEntryChange(entry.user_id, 'van_return', 0);
                              }
                            }}
                            disabled={!isEditable || entry.is_on_site || entry.is_absent}
                          />
                        </td>
                      ))}
                    </tr>

                    {/* Véhicules dynamiques avec dropdown */}
                    {selectedVehicles.map((selectedVehicle, slotIndex) => {
                      const vehicle = availableVehicles.find(v => v.id === selectedVehicle.vehicleId);
                      const vehicleName = vehicle?.name || '?';
                      return (
                        <React.Fragment key={selectedVehicle.id}>
                          {/* Véhicule aller */}
                          <tr className="field-row vehicle-row">
                            <td className="row-label vehicle-label">
                              {isEditable ? (
                                <select
                                  className="vehicle-select"
                                  value={selectedVehicle.vehicleId}
                                  onChange={(e) => changeVehicle(slotIndex, e.target.value)}
                                >
                                  <option value="">-- Véhicule --</option>
                                  {availableVehicles.map(v => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="vehicle-name">{vehicleName}</span>
                              )}
                              <span className="direction-arrow">→</span>
                            </td>
                            {entries.map(entry => {
                              const entryVehicle = (entry.vehicles || []).find(v => v.slot === slotIndex);
                              const isChecked = entryVehicle?.is_outbound || false;
                              return (
                                <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      handleVehicleChange(entry.user_id, slotIndex, 'is_outbound', e.target.checked);
                                      // Si véhicule coché, désactiver camion aller
                                      if (e.target.checked && entry.truck_outbound) {
                                        handleEntryChange(entry.user_id, 'truck_outbound', false);
                                      }
                                    }}
                                    disabled={!isEditable || entry.is_on_site || entry.is_absent || !selectedVehicle.vehicleId}
                                  />
                                </td>
                              );
                            })}
                          </tr>

                          {/* Véhicule retour */}
                          <tr className="field-row vehicle-row">
                            <td className="row-label vehicle-label">
                              <span className="vehicle-name-placeholder"></span>
                              <span className="direction-arrow">←</span>
                            </td>
                            {entries.map(entry => {
                              const entryVehicle = (entry.vehicles || []).find(v => v.slot === slotIndex);
                              const isChecked = entryVehicle?.is_return || false;
                              return (
                                <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      handleVehicleChange(entry.user_id, slotIndex, 'is_return', e.target.checked);
                                      // Si véhicule coché, désactiver camion retour
                                      if (e.target.checked && entry.truck_return) {
                                        handleEntryChange(entry.user_id, 'truck_return', false);
                                      }
                                    }}
                                    disabled={!isEditable || entry.is_on_site || entry.is_absent || !selectedVehicle.vehicleId}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </>
            )}

            {/* Séparateur horaires - accordéon */}
            <tr className="section-header section-accordion" onClick={() => toggleSection('horaires')}>
              <td colSpan={entries.length + 1}>
                <span className={`accordion-icon ${expandedSections.horaires ? 'expanded' : ''}`}>▶</span>
                Horaires
              </td>
            </tr>

            {expandedSections.horaires && (
              <>
                {/* Départ dépôt */}
                <tr className="field-row">
                  <td className="row-label">
                    Départ dépôt
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('departure_time')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => {
                    const blockedByHotelYesterday = hadHotelNightYesterday[entry.user_id];
                    return (
                      <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''} ${blockedByHotelYesterday ? 'blocked-hotel' : ''}`}>
                        {entry.is_manager ? (
                          <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                        ) : blockedByHotelYesterday ? (
                          <span className="hotel-blocked" title="Nuit hôtel la veille - pas de départ dépôt">🏨</span>
                        ) : (
                          <div className="time-input-wrapper">
                            <input
                              type="time"
                              value={entry.departure_time || ''}
                              onChange={(e) => handleEntryChange(entry.user_id, 'departure_time', e.target.value)}
                              disabled={!isEditable || entry.is_absent}
                            />
                            {isEditable && entry.departure_time && !entry.is_absent && (
                              <button
                                type="button"
                                className="clear-time-btn"
                                onClick={() => handleEntryChange(entry.user_id, 'departure_time', '')}
                                title="Effacer"
                              >×</button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Arrivée chantier 1 */}
                <tr className="field-row">
                  <td className="row-label">
                    Arrivée chantier 1
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('arrival_site_1')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                      ) : (
                        <div className="time-input-wrapper">
                          <input
                            type="time"
                            value={entry.arrival_site_1 || ''}
                            onChange={(e) => handleEntryChange(entry.user_id, 'arrival_site_1', e.target.value)}
                            disabled={!isEditable || entry.is_absent}
                          />
                          {isEditable && entry.arrival_site_1 && !entry.is_absent && (
                            <button
                              type="button"
                              className="clear-time-btn"
                              onClick={() => handleEntryChange(entry.user_id, 'arrival_site_1', '')}
                              title="Effacer"
                            >×</button>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Départ chantier 1 */}
                <tr className="field-row">
                  <td className="row-label">
                    Départ chantier 1
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('departure_site_1')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                      ) : (
                        <div className="time-input-wrapper">
                          <input
                            type="time"
                            value={entry.departure_site_1 || ''}
                            onChange={(e) => handleEntryChange(entry.user_id, 'departure_site_1', e.target.value)}
                            disabled={!isEditable || entry.is_absent}
                          />
                          {isEditable && entry.departure_site_1 && !entry.is_absent && (
                            <button
                              type="button"
                              className="clear-time-btn"
                              onClick={() => handleEntryChange(entry.user_id, 'departure_site_1', '')}
                              title="Effacer"
                            >×</button>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Arrivée chantier 2 */}
                <tr className="field-row">
                  <td className="row-label">
                    Arrivée chantier 2
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('arrival_site_2')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                      ) : (
                        <div className="time-input-wrapper">
                          <input
                            type="time"
                            value={entry.arrival_site_2 || ''}
                            onChange={(e) => handleEntryChange(entry.user_id, 'arrival_site_2', e.target.value)}
                            disabled={!isEditable || entry.is_absent}
                          />
                          {isEditable && entry.arrival_site_2 && !entry.is_absent && (
                            <button
                              type="button"
                              className="clear-time-btn"
                              onClick={() => handleEntryChange(entry.user_id, 'arrival_site_2', '')}
                              title="Effacer"
                            >×</button>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Départ chantier 2 */}
                <tr className="field-row">
                  <td className="row-label">
                    Départ chantier 2
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('departure_site_2')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                      ) : (
                        <div className="time-input-wrapper">
                          <input
                            type="time"
                            value={entry.departure_site_2 || ''}
                            onChange={(e) => handleEntryChange(entry.user_id, 'departure_site_2', e.target.value)}
                            disabled={!isEditable || entry.is_absent}
                          />
                          {isEditable && entry.departure_site_2 && !entry.is_absent && (
                            <button
                              type="button"
                              className="clear-time-btn"
                              onClick={() => handleEntryChange(entry.user_id, 'departure_site_2', '')}
                              title="Effacer"
                            >×</button>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Arrivée dépôt */}
                <tr className="field-row">
                  <td className="row-label">
                    Arrivée dépôt
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('arrival_depot')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => {
                    const blockedByHotelTonight = entry.hotel_night;
                    return (
                      <td key={entry.user_id} className={`cell-time ${entry.is_absent ? 'is-absent' : ''} ${blockedByHotelTonight ? 'blocked-hotel' : ''}`}>
                        {entry.is_manager ? (
                          <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                        ) : blockedByHotelTonight ? (
                          <span className="hotel-blocked" title="Nuit hôtel ce soir - pas de retour dépôt">🏨</span>
                        ) : (
                          <div className="time-input-wrapper">
                            <input
                              type="time"
                              value={entry.arrival_depot || ''}
                              onChange={(e) => handleEntryChange(entry.user_id, 'arrival_depot', e.target.value)}
                              disabled={!isEditable || entry.is_absent}
                            />
                            {isEditable && entry.arrival_depot && !entry.is_absent && (
                              <button
                                type="button"
                                className="clear-time-btn"
                                onClick={() => handleEntryChange(entry.user_id, 'arrival_depot', '')}
                                title="Effacer"
                              >×</button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </>
            )}

            {/* Séparateur EPI - accordéon */}
            <tr className="section-header section-accordion" onClick={() => toggleSection('epi')}>
              <td colSpan={entries.length + 1}>
                <span className={`accordion-icon ${expandedSections.epi ? 'expanded' : ''}`}>▶</span>
                EPI
              </td>
            </tr>

            {expandedSections.epi && (
              <>
                {/* Chaussures sécu */}
                <tr className="field-row">
                  <td className="row-label">
                    Chaussures sécu
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('safety_shoes', true)} title="Tous OK">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-epi ${entry.is_absent ? 'is-absent' : ''}`}>
                      <div className="epi-buttons">
                        <button
                          type="button"
                          className={`epi-btn ${entry.safety_shoes === true ? 'yes' : ''}`}
                          onClick={() => handleEntryChange(entry.user_id, 'safety_shoes', entry.safety_shoes === true ? null : true)}
                          disabled={!isEditable || entry.is_absent}
                        >✓</button>
                        <button
                          type="button"
                          className={`epi-btn ${entry.safety_shoes === false ? 'no' : ''}`}
                          onClick={() => {
                            if (entry.safety_shoes === false && entry.safety_shoes_refusal_acknowledged) {
                              // Refus signé → consulter la signature
                              setEpiRefusalTarget({
                                userId: entry.user_id,
                                userName: entry.user_name || entry.first_name || 'Participant',
                                epiType: 'shoes',
                                viewData: {
                                  signature: entry.safety_shoes_refusal_signature,
                                  date: entry.safety_shoes_refusal_date,
                                  acknowledged: true
                                }
                              });
                            } else if (entry.safety_shoes === false) {
                              // Refus non signé → annuler le refus
                              handleEntryChange(entry.user_id, 'safety_shoes', null);
                            } else {
                              // Nouveau refus → ouvrir la modale signature
                              setEpiRefusalTarget({ userId: entry.user_id, userName: entry.user_name || entry.first_name || 'Participant', epiType: 'shoes' });
                            }
                          }}
                          disabled={(!isEditable && !entry.safety_shoes_refusal_acknowledged) || entry.is_absent}
                          title={entry.safety_shoes_refusal_acknowledged ? 'Cliquer pour voir la signature' : ''}
                        >{entry.safety_shoes_refusal_acknowledged && entry.safety_shoes === false ? '✗✍' : '✗'}</button>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Protection auditive */}
                <tr className="field-row">
                  <td className="row-label">
                    Protection auditive
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('hearing_protection', true)} title="Tous OK">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-epi ${entry.is_absent ? 'is-absent' : ''}`}>
                      <div className="epi-buttons">
                        <button
                          type="button"
                          className={`epi-btn ${entry.hearing_protection === true ? 'yes' : ''}`}
                          onClick={() => handleEntryChange(entry.user_id, 'hearing_protection', entry.hearing_protection === true ? null : true)}
                          disabled={!isEditable || entry.is_absent}
                        >✓</button>
                        <button
                          type="button"
                          className={`epi-btn ${entry.hearing_protection === false ? 'no' : ''}`}
                          onClick={() => {
                            if (entry.hearing_protection === false && entry.hearing_protection_refusal_acknowledged) {
                              // Refus signé → consulter la signature
                              setEpiRefusalTarget({
                                userId: entry.user_id,
                                userName: entry.user_name || entry.first_name || 'Participant',
                                epiType: 'hearing',
                                viewData: {
                                  signature: entry.hearing_protection_refusal_signature,
                                  date: entry.hearing_protection_refusal_date,
                                  acknowledged: true
                                }
                              });
                            } else if (entry.hearing_protection === false) {
                              // Refus non signé → annuler le refus
                              handleEntryChange(entry.user_id, 'hearing_protection', null);
                            } else {
                              // Nouveau refus → ouvrir la modale signature
                              setEpiRefusalTarget({ userId: entry.user_id, userName: entry.user_name || entry.first_name || 'Participant', epiType: 'hearing' });
                            }
                          }}
                          disabled={(!isEditable && !entry.hearing_protection_refusal_acknowledged) || entry.is_absent}
                          title={entry.hearing_protection_refusal_acknowledged ? 'Cliquer pour voir la signature' : ''}
                        >{entry.hearing_protection_refusal_acknowledged && entry.hearing_protection === false ? '✗✍' : '✗'}</button>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Heure d'attente / Observation - champ texte libre global */}
                <tr className="field-row observation-row">
                  <td className="row-label">Attente / Observation</td>
                  <td colSpan={entries.length} className="cell-observation">
                    {isEditable ? (
                      <input
                        type="text"
                        value={headerData.observation || ''}
                        onChange={(e) => handleHeaderChange('observation', e.target.value)}
                        placeholder="Heure d'attente, observations..."
                        className="observation-input"
                      />
                    ) : (
                      <span className="value">{headerData.observation || '-'}</span>
                    )}
                  </td>
                </tr>
              </>
            )}

            {/* Séparateur Repas - accordéon */}
            <tr className="section-header section-accordion" onClick={() => toggleSection('repas')}>
              <td colSpan={entries.length + 1}>
                <span className={`accordion-icon ${expandedSections.repas ? 'expanded' : ''}`}>▶</span>
                Repas / Hébergement
              </td>
            </tr>

            {expandedSections.repas && (
              <>
                {/* Repas Gamelle */}
                <tr className="field-row">
                  <td className="row-label">
                    Repas gamelle
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('packed_lunch', true)} title="Cocher tous">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                      <input
                        type="checkbox"
                        checked={entry.packed_lunch || false}
                        onChange={(e) => handleEntryChange(entry.user_id, 'packed_lunch', e.target.checked)}
                        disabled={!isEditable || entry.is_absent}
                      />
                    </td>
                  ))}
                </tr>

                {/* Repas resto midi */}
                <tr className="field-row">
                  <td className="row-label">
                    Repas resto midi
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('restaurant_lunch', true)} title="Cocher tous">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                      <input
                        type="checkbox"
                        checked={entry.restaurant_lunch || false}
                        onChange={(e) => handleEntryChange(entry.user_id, 'restaurant_lunch', e.target.checked)}
                        disabled={!isEditable || entry.is_absent}
                      />
                    </td>
                  ))}
                </tr>

                {/* Repas soir */}
                <tr className="field-row">
                  <td className="row-label">
                    Repas soir
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('dinner', true)} title="Cocher tous">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                      <input
                        type="checkbox"
                        checked={entry.dinner || false}
                        onChange={(e) => handleEntryChange(entry.user_id, 'dinner', e.target.checked)}
                        disabled={!isEditable || entry.is_absent}
                      />
                    </td>
                  ))}
                </tr>

                {/* Nuit hôtel */}
                <tr className="field-row">
                  <td className="row-label">
                    Nuit hôtel
                    {isEditable && <button className="apply-all-btn" onClick={() => applyToAll('hotel_night', true)} title="Cocher tous">✓</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-checkbox ${entry.is_absent ? 'is-absent' : ''}`}>
                      <input
                        type="checkbox"
                        checked={entry.hotel_night || false}
                        onChange={(e) => handleEntryChange(entry.user_id, 'hotel_night', e.target.checked)}
                        disabled={!isEditable || entry.is_absent}
                      />
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* Séparateur Heures - accordéon */}
            <tr className="section-header totals section-accordion" onClick={() => toggleSection('heures')}>
              <td colSpan={entries.length + 1}>
                <span className={`accordion-icon ${expandedSections.heures ? 'expanded' : ''}`}>▶</span>
                Heures
              </td>
            </tr>

            {expandedSections.heures && (
              <>
                {/* Heures travail */}
                <tr className="field-row calculated">
                  <td className="row-label">Heures travail</td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className="cell-hours">
                      {entry.is_manager
                        ? <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                        : (entry.work_hours ? parseFloat(entry.work_hours).toFixed(2) + 'h' : '-')
                      }
                    </td>
                  ))}
                </tr>

                {/* Heures route */}
                <tr className="field-row calculated">
                  <td className="row-label">Heures route</td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className="cell-hours">
                      {entry.is_manager
                        ? <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                        : (entry.travel_hours ? parseFloat(entry.travel_hours).toFixed(2) + 'h' : '-')
                      }
                    </td>
                  ))}
                </tr>

                {/* Heures route spécifiques (bonus manuel, visible uniquement pour les validateurs) */}
                {isValidator && (
                  <tr className="field-row">
                    <td className="row-label" title="Heures de route bonus saisies manuellement (ex: 25% fourgon suivant camion)">
                      H. route spéc.
                      {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('specific_travel_hours')} title="Copier à tous">→</button>}
                    </td>
                    {entries.map(entry => (
                      <td key={entry.user_id} className={`cell-input ${entry.is_absent ? 'is-absent' : ''}`}>
                        {entry.is_manager ? (
                          <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                        ) : (
                          <input
                            type="number"
                            value={entry.specific_travel_hours || ''}
                            onChange={(e) => handleEntryChange(entry.user_id, 'specific_travel_hours', e.target.value)}
                            disabled={!isEditable || entry.is_absent}
                            step="0.25"
                            min="0"
                            placeholder="0"
                            style={{ color: '#E65100' }}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Heures diverses */}
                <tr className="field-row">
                  <td className="row-label">
                    Heures diverses
                    {isEditable && <button className="apply-all-btn" onClick={() => copyFirstToAll('misc_hours')} title="Copier à tous">→</button>}
                  </td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-input ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours">P</strong>
                      ) : (
                        <input
                          type="number"
                          value={entry.misc_hours || ''}
                          onChange={(e) => handleEntryChange(entry.user_id, 'misc_hours', e.target.value)}
                          disabled={!isEditable || entry.is_absent}
                          step="0.25"
                          min="0"
                          placeholder="0"
                        />
                      )}
                    </td>
                  ))}
                </tr>

                {/* Total */}
                <tr className="field-row total-row">
                  <td className="row-label">TOTAL</td>
                  {entries.map(entry => (
                    <td key={entry.user_id} className={`cell-total ${entry.is_absent ? 'is-absent' : ''}`}>
                      {entry.is_absent ? (
                        <strong style={{ color: '#dc3545' }}>ABSENT</strong>
                      ) : entry.is_manager ? (
                        <strong style={{ color: '#1976D2' }} title="Cadre forfait jours - Présent">P</strong>
                      ) : (
                        <strong>{entry.total_hours ? parseFloat(entry.total_hours).toFixed(2) + 'h' : '-'}</strong>
                      )}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Bouton ajouter intérimaire */}
      {isEditable && (
        <div className="add-interim-section">
          <button
            type="button"
            className="add-interim-btn"
            onClick={() => setShowInterimForm(true)}
          >
            ➕ Ajouter un intérimaire
          </button>
        </div>
      )}

      <WorksheetFooter
        entries={entries}
        onSave={handleSave}
        onSubmit={handleSubmit}
        onValidate={handleValidate}
        onInvalidate={handleInvalidate}
        onCheck={handleCheck}
        onClose={onClose}
        saving={saving}
        isDirty={isDirty}
        isEditable={isEditable}
        isValidator={isValidator}
        status={currentStatus}
        worksheetId={worksheet?.id}
      />

      {/* Modale ajout intérimaire */}
      {showInterimForm && (
        <QuickInterimForm
          onClose={() => setShowInterimForm(false)}
          onSuccess={handleInterimCreated}
          onSelectExisting={handleSelectExistingInterim}
          pendingReview={true}
        />
      )}

      {epiRefusalTarget && (
        <EPIRefusalModal
          userName={epiRefusalTarget.userName}
          epiType={epiRefusalTarget.epiType || 'hearing'}
          viewData={epiRefusalTarget.viewData}
          onCancel={() => setEpiRefusalTarget(null)}
          onDelete={epiRefusalTarget.viewData && isEditable ? () => {
            const isShoes = epiRefusalTarget.epiType === 'shoes';
            const fieldName = isShoes ? 'safety_shoes' : 'hearing_protection';
            const prefix = isShoes ? 'safety_shoes_refusal' : 'hearing_protection_refusal';
            handleEntryChange(epiRefusalTarget.userId, fieldName, null);
            setEntries(prev => prev.map(entry => {
              if (entry.user_id === epiRefusalTarget.userId) {
                return {
                  ...entry,
                  [`${prefix}_signature`]: null,
                  [`${prefix}_date`]: null,
                  [`${prefix}_acknowledged`]: false
                };
              }
              return entry;
            }));
            setIsDirty(true);
            setEpiRefusalTarget(null);
          } : undefined}
          onConfirm={(refusalData) => {
            const isShoes = epiRefusalTarget.epiType === 'shoes';
            const fieldName = isShoes ? 'safety_shoes' : 'hearing_protection';
            const prefix = isShoes ? 'safety_shoes_refusal' : 'hearing_protection_refusal';
            handleEntryChange(epiRefusalTarget.userId, fieldName, false);
            setEntries(prev => prev.map(entry => {
              if (entry.user_id === epiRefusalTarget.userId) {
                return {
                  ...entry,
                  [`${prefix}_signature`]: refusalData.signature,
                  [`${prefix}_date`]: refusalData.date,
                  [`${prefix}_acknowledged`]: true
                };
              }
              return entry;
            }));
            setIsDirty(true);
            setEpiRefusalTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default DailyWorksheet;
